import { inject, injectable } from "inversify";
import {
  IDefaultInsightsIAResponse,
  InsightsAIDreGateway,
  TaceitarTermoPayload,
  TAgenteDeDreResponse,
  TAvaliarInsightPayload,
  TAvaliarInsightResponse,
  TObterConfiguracaoResponse,
  TObterInsightsRelatorioResponse,
  TTermoDeUsoResponse,
  TVerificarTermoUsoPayload,
  TVerificarTermoUsoResponse
} from "./InsightsAIDreGateway";
import { DITypes } from "@/core/@types/types";
import type { HttpClient } from "@/core/services/protocols/types";
import Result from "@/core/common/Result";
import { getSimpleErrorMessage } from "@/core/errors/catchErrorHander";
import { TAnaliseInteligenteInsightsStoreFiltros } from "../store/modules/InsightsDreAnaliseInteligente";

@injectable()
export default class InsightsAIDreGatewayAdapter implements InsightsAIDreGateway {
  @inject(DITypes.INDICADORES_HTTP_CLIENT)
  private http!: HttpClient;

  async verificar(
    params: TVerificarTermoUsoPayload
  ): Promise<Result<TVerificarTermoUsoResponse>> {
    try {
      const response = await this.http.get<
        IDefaultInsightsIAResponse<TVerificarTermoUsoResponse>
      >({
        url: "/agente-dre/AgenteDRE/v1/termo-uso/verificar",
        queryParams: params
      });

      if (response.statusCode !== 200) {
        return Result.fail("Erro ao verificar termo de uso");
      }

      if (response.body?.code !== 200) {
        return Result.fail(
          response.body?.errors?.[0]?.message ||
            "Erro ao verificar termo de uso"
        );
      }

      return Result.ok(response.body?.result);
    } catch {
      return Result.fail("Erro ao verificar termo de uso");
    }
  }

  async gerarRelatorio(): Promise<Result<TObterInsightsRelatorioResponse>> {
    try {
      const response = await this.http.get<
        IDefaultInsightsIAResponse<TObterInsightsRelatorioResponse>
      >({
        url: "/agente-dre/AgenteDRE/v1/obter-relatorio"
      });

      if (response.statusCode !== 200) {
        return Result.fail("");
      }

      if (response.body?.code !== 200) {
        return Result.fail(
          response.body?.errors?.[0]?.message || "Erro ao obter relatorio"
        );
      }

      return Result.ok(response.body?.result);
    } catch (error) {
      return Result.fail(getSimpleErrorMessage(error));
    }
  }

  async aceitarTermo(
    params: TaceitarTermoPayload
  ): Promise<Result<IDefaultInsightsIAResponse<string | undefined>>> {
    try {
      const response = await this.http.post<
        IDefaultInsightsIAResponse<string | undefined>
      >({
        url: "/agente-dre/AgenteDRE/v1/termo-uso/aceitar",
        body: params
      });

      if (response.statusCode !== 200) {
        return Result.fail("");
      }

      if (response.body?.code !== 200) {
        return Result.fail(
          response.body?.errors?.[0]?.message || "Erro ao obter relatorio"
        );
      }

      return Result.ok(response.body);
    } catch (error) {
      return Result.fail(getSimpleErrorMessage(error));
    }
  }

  async avaliarInsight(
    params: TAvaliarInsightPayload
  ): Promise<Result<string | undefined>> {
    try {
      const response = await this.http.post<string | undefined>({
        url: "/agente-dre/AgenteDRE/v1/avaliar-insight",
        body: params
      });

      if (response.statusCode !== 200) {
        return Result.fail("");
      }

      return Result.ok(response.body);
    } catch (error) {
      return Result.fail(getSimpleErrorMessage(error));
    }
  }

  async termoDeUso(): Promise<
    Result<IDefaultInsightsIAResponse<TTermoDeUsoResponse>>
  > {
    try {
      const response = await this.http.get<
        IDefaultInsightsIAResponse<TTermoDeUsoResponse>
      >({
        url: "/agente-dre/AgenteDRE/v1/termo-uso"
      });

      if (response.statusCode !== 200) {
        return Result.fail("Erro ao obter termo de uso");
      }

      if (response.body?.code !== 200) {
        return Result.fail(
          response.body?.errors?.[0]?.message || "Erro ao obter termo de uso"
        );
      }

      return Result.ok(response.body);
    } catch (error) {
      return Result.fail(getSimpleErrorMessage(error));
    }
  }

  async agenteDeDre(): Promise<Result<TAgenteDeDreResponse[]>> {
    try {
      const response = await this.http.get<
        IDefaultInsightsIAResponse<TAgenteDeDreResponse[]>
      >({
        url: "/agente-dre/AgenteDRE/v1/agentes-dre"
      });

      if (response.statusCode !== 200) {
        return Result.fail("Erro ao obter agente de dre");
      }

      return Result.ok(response.body?.result);
    } catch (error) {
      return Result.fail(getSimpleErrorMessage(error));
    }
  }

  async agenteDeDreFiltros(
    filtros: TAnaliseInteligenteInsightsStoreFiltros
  ): Promise<Result<IDefaultInsightsIAResponse<TAvaliarInsightResponse>>> {
    try {
      const response = await this.http.post<
        IDefaultInsightsIAResponse<TAvaliarInsightResponse>
      >({
        url: "/agente-dre/AgenteDRE/v1/agentes-dre",
        body: {
          IdDaEmpresa: filtros.IdDaEmpresa,
          DataDeCompetencia: filtros.DataDeCompetencia,
          ResumoDoGrupo: filtros.ResumoDoGrupo
        }
      });

      if (response.statusCode !== 200) {
        return Result.fail("Erro ao obter agente de dre");
      }

      return Result.ok(response.body);
    } catch (error) {
      return Result.fail(getSimpleErrorMessage(error));
    }
  }

  async obterConfiguracao(): Promise<Result<TObterConfiguracaoResponse>> {
    const response = await this.http.get<
      IDefaultInsightsIAResponse<TObterConfiguracaoResponse>
    >({
      url: "/agente-dre/AgenteDRE/v1/configuracao-relatorio"
    });

    if (response.statusCode !== 200) {
      return Result.fail("Erro ao salvar configuração");
    }

    return Result.ok(response.body?.result);
  }
}
